// =====================================================================
// WeddingOS – /venue  (Venue-Ansicht)
// =====================================================================
// Fokussierter Screen für Hochzeitslokal-Betreiber.
// Token-Pattern identisch zu DJ/Barkeeper.
// Tabs: Bestellungen | Tischbelegung | Zeitplan
// Read-only – Venue kann nichts ändern.
// =====================================================================

import { useState, useEffect } from "react";
import { supabase, realtime } from "../../services/supabase";
import "../../styles/theme.css";

const VENUE_TOKEN_KEY = "wos_venue_token";

const STATUS_CFG = {
  ordered:   { label: "Bestellt",  color: "var(--ink-muted)", bg: "var(--surface)" },
  preparing: { label: "Bestellt",  color: "var(--ink-muted)", bg: "var(--surface)" },
  ready:     { label: "Fertig! ✦", color: "var(--forest)",    bg: "var(--forest-soft)" },
};

export default function VenueView() {
  const [token] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) sessionStorage.setItem(VENUE_TOKEN_KEY, t);
    return t ?? sessionStorage.getItem(VENUE_TOKEN_KEY) ?? null;
  });

  const [ctx,      setCtx]      = useState(null);
  const [orders,   setOrders]   = useState([]);
  const [guests,   setGuests]   = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [active,   setActive]   = useState("orders");
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // ── Token → Context laden ─────────────────────────────────────────
  useEffect(() => {
    if (!token) { setError("Kein Zugangscode vorhanden."); setLoading(false); return; }
    loadAll();
  }, [token]);

  async function loadAll() {
    try {
      const [
        { data: ctxData,  error: e1 },
        { data: ordData,  error: e2 },
        { data: gstData,  error: e3 },
        { data: tlData,   error: e4 },
      ] = await Promise.all([
        supabase.rpc("venue_get_context",  { p_token: token }),
        supabase.rpc("venue_get_orders",   { p_token: token }),
        supabase.rpc("venue_get_guests",   { p_token: token }),
        supabase.rpc("venue_get_timeline", { p_token: token }),
      ]);

      if (e1) throw e1;
      const c = Array.isArray(ctxData) ? ctxData[0] : ctxData;
      if (!c) throw new Error("invalid_token");

      setCtx(c);
      setOrders(ordData ?? []);
      setGuests(gstData ?? []);
      setTimeline(tlData ?? []);
    } catch (err) {
      setError(
        err.message?.includes("invalid_token")
          ? "Ungültiger oder abgelaufener Venue-Link."
          : "Verbindungsfehler. Bitte Seite neu laden."
      );
    } finally {
      setLoading(false);
    }
  }

  // ── Realtime: Bestellungen live ───────────────────────────────────
  useEffect(() => {
    if (!ctx?.wedding_id) return;
    const unsub = realtime.drinkOrders(ctx.wedding_id, (payload) => {
      setOrders((prev) => {
        if (payload.eventType === "INSERT") return [payload.new, ...prev].filter(o => o.status !== "delivered");
        if (payload.eventType === "UPDATE") {
          if (payload.new.status === "delivered") return prev.filter(o => o.id !== payload.new.id);
          return prev.map(o => o.id === payload.new.id ? { ...o, ...payload.new } : o);
        }
        return prev;
      });
    });
    return unsub;
  }, [ctx?.wedding_id]);

  // ── Loading / Error States ────────────────────────────────────────
  if (!token) return (
    <Shell>
      <NoToken />
    </Shell>
  );

  if (loading) return (
    <Shell>
      <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
        <Spinner />
      </div>
    </Shell>
  );

  if (error) return (
    <Shell>
      <div style={styles.errorBox}>
        <p style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", marginBottom: 8 }}>
          Zugang nicht möglich
        </p>
        <p style={{ fontSize: 14, color: "var(--ink-muted)" }}>{error}</p>
      </div>
    </Shell>
  );

  // ── Daten aufbereiten ─────────────────────────────────────────────
  const daysUntil   = Math.ceil((new Date(ctx.event_date) - Date.now()) / 86_400_000);
  const dateLabel   = daysUntil > 0
    ? (daysUntil === 1 ? "Morgen!" : `In ${daysUntil} Tagen`)
    : daysUntil === 0 ? "Heute 🎉" : formatDate(ctx.event_date);

  // Gäste nach Tisch gruppieren
  const byTable = guests.reduce((acc, g) => {
    const t = g.table_number ?? "–";
    if (!acc[t]) acc[t] = [];
    acc[t].push(g);
    return acc;
  }, {});
  const tableKeys = Object.keys(byTable).sort((a, b) => Number(a) - Number(b));

  const openCount = orders.filter(o => o.status !== "delivered").length;

  return (
    <Shell>
      {/* ── Header ── */}
      <header style={styles.header}>
        <p style={styles.monogram}>WeddingOS · Venue</p>
        <p style={styles.eyebrow}>{dateLabel}</p>
        <h1 style={styles.display}>{ctx.wedding_name}</h1>
        {ctx.location && <p style={styles.meta}>{ctx.location}</p>}
      </header>

      {/* ── Tabs ── */}
      <div style={styles.tabs}>
        <TabBtn
          active={active === "orders"}
          onClick={() => setActive("orders")}
          badge={openCount > 0 ? openCount : null}
        >
          Bestellungen
        </TabBtn>
        <TabBtn
          active={active === "guests"}
          onClick={() => setActive("guests")}
        >
          Tischbelegung
        </TabBtn>
        <TabBtn
          active={active === "timeline"}
          onClick={() => setActive("timeline")}
          badge={timeline.length > 0 ? timeline.length : null}
        >
          Zeitplan
        </TabBtn>
      </div>

      {/* ── Bestellungen ── */}
      {active === "orders" && (
        <div>
          {orders.length === 0 ? (
            <EmptyState icon="◈" text="Keine aktiven Bestellungen." />
          ) : (
            <>
              {/* Fertige zuerst */}
              {["ready", "preparing", "ordered"].map((status) => {
                const items = orders.filter(o => o.status === status);
                if (!items.length) return null;
                const cfg = STATUS_CFG[status];
                return (
                  <div key={status} style={{ marginBottom: 24 }}>
                    <div style={styles.statusHeader}>
                      <span style={{ color: cfg.color, fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>
                        {cfg.label.toUpperCase()}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--ink-subtle)" }}>
                        {items.length} {items.length === 1 ? "Bestellung" : "Bestellungen"}
                      </span>
                    </div>
                    {items.map(order => (
                      <OrderCard key={order.id} order={order} cfg={cfg} />
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ── Tischbelegung ── */}
      {active === "guests" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {tableKeys.map(table => (
              <div key={table} style={styles.tableCard}>
                <div style={styles.tableNum}>Tisch {table}</div>
                <div style={styles.tableCount}>{byTable[table].length} Pers.</div>
                <div style={{ marginTop: 8 }}>
                  {byTable[table].map(g => (
                    <div key={g.name} style={{ fontSize: 12, color: "var(--ink-muted)", padding: "2px 0" }}>
                      {g.name}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={styles.totalBadge}>
            <span style={{ fontFamily: "var(--font-display)", fontSize: "1.8rem", color: "var(--accent)" }}>
              {guests.length}
            </span>
            <span style={{ fontSize: 13, color: "var(--ink-muted)", marginLeft: 8 }}>
              Gäste gesamt · {tableKeys.length} Tische
            </span>
          </div>
        </div>
      )}

      {/* ── Zeitplan ── */}
      {active === "timeline" && (
        <div>
          {timeline.length === 0 ? (
            <EmptyState icon="◎" text="Keine geplanten Ereignisse." />
          ) : (
            timeline.map((item, i) => (
              <div key={item.id} style={styles.timelineItem}>
                <div style={styles.timelineTime}>
                  <span style={styles.timeHour}>{formatTimeOnly(item.scheduled_at)}</span>
                  <span style={styles.timeUntil}>{timeUntil(item.scheduled_at)}</span>
                </div>
                <div style={{
                  ...styles.timelineLine,
                  background: i === 0 ? "var(--accent)" : "var(--line)",
                }} />
                <div style={styles.timelineContent}>
                  <p style={{ margin: 0, fontSize: 14, color: "var(--ink)", lineHeight: 1.5 }}>
                    {item.message}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <footer style={styles.footer}>
        <p style={styles.eyebrow}>WeddingOS · Venue-Ansicht (read-only)</p>
      </footer>
    </Shell>
  );
}

// ── Sub-Components ────────────────────────────────────────────────────

function Shell({ children }) {
  return (
    <div style={{
      minHeight:     "100dvh",
      background:    "var(--bg)",
      fontFamily:    "var(--font-body)",
      color:         "var(--ink)",
      maxWidth:      520,
      margin:        "0 auto",
      width:         "100%",
      padding:       "24px 20px 60px",
      boxSizing:     "border-box",
    }}>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, children, badge }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding:      "8px 14px",
        borderRadius: "999px",
        border:       `1px solid ${active ? "var(--accent)" : "var(--line)"}`,
        background:   active ? "var(--accent)" : "var(--surface)",
        color:        active ? "#fff" : "var(--ink-muted)",
        fontSize:     12,
        cursor:       "pointer",
        fontFamily:   "var(--font-body)",
        fontWeight:   active ? 600 : 400,
        transition:   "all 0.15s",
        display:      "flex",
        alignItems:   "center",
        gap:          6,
      }}
    >
      {children}
      {badge !== null && badge !== undefined && (
        <span style={{
          background:   active ? "rgba(255,255,255,0.3)" : "var(--accent)",
          color:        "#fff",
          borderRadius: "999px",
          padding:      "1px 6px",
          fontSize:     10,
          fontWeight:   700,
        }}>
          {badge}
        </span>
      )}
    </button>
  );
}

function OrderCard({ order, cfg }) {
  return (
    <div style={{
      padding:      "13px 16px",
      background:   cfg.bg,
      border:       `1px solid ${order.status === "ready" ? "var(--forest)" : "var(--line)"}`,
      borderRadius: "var(--radius-md)",
      marginBottom: 8,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem" }}>
          {order.quantity > 1 ? `${order.quantity}× ` : ""}{order.drink_name}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color }}>
          {cfg.label}
        </span>
      </div>
      <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>
        {order.guest?.name ?? "Gast"}
        {order.guest?.table_number && ` · Tisch ${order.guest.table_number}`}
        {" · "}{formatTime(order.created_at)}
        {order.note && (
          <span style={{ color: "var(--accent)", marginLeft: 6 }}>· {order.note}</span>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: "center", paddingTop: 48 }}>
      <p style={{ fontSize: 28, marginBottom: 12, opacity: 0.3 }}>{icon}</p>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", color: "var(--ink-muted)" }}>
        {text}
      </p>
    </div>
  );
}

function NoToken() {
  return (
    <div style={{ textAlign: "center", paddingTop: 80 }}>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", marginBottom: 12 }}>
        Kein Zugangscode
      </p>
      <p style={{ fontSize: 14, color: "var(--ink-muted)", lineHeight: 1.6 }}>
        Bitte öffne den Venue-Link den du vom Brautpaar erhalten hast.
      </p>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 6, height: 6, borderRadius: "50%",
          background: "var(--accent)", opacity: 0.5,
          animation: "wos-spin 1s ease-in-out infinite",
          animationDelay: `${i * 0.15}s`,
        }} />
      ))}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const styles = {
  header:        { marginBottom: 20 },
  monogram:      { fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 11, letterSpacing: "0.3em", color: "var(--accent)", marginBottom: 4 },
  eyebrow:       { fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--ink-subtle)", fontWeight: 500, marginBottom: 4 },
  display:       { fontFamily: "var(--font-display)", fontSize: "clamp(1.8rem,6vw,2.4rem)", fontWeight: 500, lineHeight: 1.05, margin: "0 0 4px" },
  meta:          { fontSize: 13, color: "var(--ink-muted)", margin: 0 },
  tabs:          { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 },
  statusHeader:  { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--line)" },
  tableCard:     { background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: "14px 16px" },
  tableNum:      { fontFamily: "var(--font-display)", fontSize: "1.1rem", color: "var(--accent)", marginBottom: 2 },
  tableCount:    { fontSize: 11, color: "var(--ink-subtle)", letterSpacing: "0.1em" },
  totalBadge:    { padding: "14px 16px", background: "var(--surface)", borderRadius: "var(--radius-md)", display: "flex", alignItems: "center" },
  timelineItem:  { display: "flex", gap: 12, alignItems: "flex-start", paddingBottom: 20 },
  timelineTime:  { flexShrink: 0, width: 52, display: "flex", flexDirection: "column", alignItems: "flex-end" },
  timeHour:      { fontFamily: "var(--font-display)", fontSize: "1rem", color: "var(--accent)" },
  timeUntil:     { fontSize: 10, color: "var(--ink-subtle)", marginTop: 2, textAlign: "right" },
  timelineLine:  { flexShrink: 0, width: 2, alignSelf: "stretch", minHeight: 40, marginTop: 6 },
  timelineContent: { flex: 1, paddingTop: 4 },
  errorBox:      { textAlign: "center", paddingTop: 80 },
  footer:        { textAlign: "center", paddingTop: 40 },
};

// ── Helpers ──────────────────────────────────────────────────────────

function formatDate(iso) {
  try { return new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso)); }
  catch { return iso; }
}
function formatTime(iso) {
  try { return new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso)); }
  catch { return ""; }
}
function formatTimeOnly(iso) {
  return formatTime(iso);
}
function timeUntil(iso) {
  const diff = new Date(iso) - Date.now();
  if (diff <= 0) return "jetzt";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m} Min.`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
